/**
 * Copyright (c) 2025 Stefan Radig
 * Client-side authentication handling
 */

import { isTauri } from './storageProvider';
import { isNetworkError, isServerDownStatus } from './networkErrors';
import { DebugLogger } from './debugLogger';

/**
 * How long one open-path auth request may take before it is aborted: the
 * small requests that stand between a page and its first render
 * (auth_status, init_database) and the validate/claim/regenerate/setup
 * calls. Without a limit a request that never completes (flaky cellular, a
 * VPN up with no route, a backgrounded WebView) blocks the page for good.
 * 15 s is generous for these; an abort counts as "server unreachable"
 * (isNetworkError), which callers already handle. NOT a default of
 * authenticatedFetch / fetchWithFullAuth: they also carry 5 MB upload chunks
 * and full-document downloads, which must never be cut off (opt-in there
 * with { timeoutMs }).
 */
export const AUTH_TIMEOUT_MS = 15000;

/** The Response methods that read the body. */
const BODY_READERS = ['json', 'text', 'arrayBuffer', 'blob', 'formData'] as const;

/** Opt-in request timeout for authenticatedFetch / fetchWithFullAuth. */
export interface AuthFetchTiming {
    /** Abort the request (headers and body read) after this many ms. Default: no timeout. */
    timeoutMs?: number;
}

/**
 * fetch() aborted after `timeoutMs` (rejecting with an AbortError, as fetch
 * does); without `timeoutMs` a plain fetch(). The limit covers the whole exchange: the timer runs until the
 * body has been read (a body reader of the returned Response settles), so a
 * response whose headers arrive but whose body stalls is aborted too. A
 * signal in `options` still aborts the request as well. A response whose
 * body is never read is left alone (the late abort reaches no reader).
 */
function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs?: number): Promise<Response> {
    if (timeoutMs === undefined) return fetch(url, options);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const callerSignal = options.signal;
    const forwardAbort = () => controller.abort();
    if (callerSignal) {
        if (callerSignal.aborted) controller.abort();
        else callerSignal.addEventListener('abort', forwardAbort, { once: true });
    }
    const done = () => {
        clearTimeout(timer);
        callerSignal?.removeEventListener('abort', forwardAbort);
    };
    /** Rejects with an AbortError once the request is aborted (a stalled body read must not outlive it). */
    const aborted = () => new Promise<never>((_, reject) => {
        const fail = () => reject(new DOMException('The operation was aborted.', 'AbortError'));
        if (controller.signal.aborted) fail();
        else controller.signal.addEventListener('abort', fail, { once: true });
    });
    return fetch(url, { ...options, signal: controller.signal }).then(response => {
        for (const reader of BODY_READERS) {
            const read = (response[reader] as () => Promise<unknown>).bind(response);
            Object.defineProperty(response, reader, {
                value: () => Promise.race([read(), aborted()]).finally(done),
                configurable: true,
            });
        }
        return response;
    }, error => {
        done();
        throw error;
    });
}

/**
 * Error class for authentication failures
 */
export class AuthError extends Error {
    public statusCode: number;

    constructor(message: string, statusCode: number = 401) {
        super(message);
        this.name = 'AuthError';
        this.statusCode = statusCode;
    }
}

export class AuthManager {
    private baseUrl: string;
    private jwt: string | null = null;
    private apiToken: string | null = null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        this.loadJWT();
        this.loadAPIToken();
    }

    // Load JWT from localStorage
    private loadJWT(): void {
        this.jwt = localStorage.getItem('editoor_jwt');
    }

    // Save JWT to localStorage
    private saveJWT(jwt: string): void {
        this.jwt = jwt;
        localStorage.setItem('editoor_jwt', jwt);
    }

    // Load API token from localStorage
    private loadAPIToken(): void {
        this.apiToken = localStorage.getItem('editoor_api_token');
    }

    // Save API token to localStorage
    private saveAPIToken(token: string): void {
        this.apiToken = token;
        localStorage.setItem('editoor_api_token', token);
    }

    // Clear JWT and API token
    public clearJWT(): void {
        this.jwt = null;
        localStorage.removeItem('editoor_jwt');
    }

    // Clear API token (for logout)
    public clearAPIToken(): void {
        this.apiToken = null;
        localStorage.removeItem('editoor_api_token');
        this.clearJWT();
    }

    // Check if user is authenticated (has API token)
    public isAuthenticated(): boolean {
        return this.apiToken !== null && this.apiToken !== '';
    }

    // Update UI to show auth status
    public updateAuthUI(): void {
        const userDropdown = document.getElementById('user-dropdown');
        const loginBtn = document.getElementById('login-btn');

        if (this.isAuthenticated()) {
            // Show user dropdown with username
            if (userDropdown) userDropdown.style.display = 'inline-block';
            if (loginBtn) loginBtn.style.display = 'none';
        } else {
            // Hide user dropdown, show login button
            if (userDropdown) userDropdown.style.display = 'none';
            if (loginBtn) loginBtn.style.display = 'block';
        }
    }

    // Update username display in UI
    public updateUserNameUI(userName: string): void {
        const userNameEl = document.getElementById('auth-username');
        if (userNameEl) {
            userNameEl.textContent = userName;
        }
    }

    // Logout - clear all auth data
    public logout(): void {
        console.log('[AUTH] Logging out');
        this.clearAPIToken();
        this.updateAuthUI();
    }

    // Check if authentication is required.
    // Fails CLOSED: if the server can't be reached, fall back to the last
    // known value, and require auth when there is none - a network failure
    // (or an attacker blocking auth_status.php) must not grant access.
    async checkAuthStatus(): Promise<boolean> {
        // In Tauri mode, no server auth is required
        if (isTauri()) {
            return false;
        }

        const attempts = navigator.onLine ? 3 : 1;
        for (let attempt = 0; attempt < attempts; attempt++) {
            if (attempt > 0) {
                await new Promise(resolve => setTimeout(resolve, 500 * attempt));
            }
            try {
                const response = await fetchWithTimeout(`${this.baseUrl}/php/auth_status.php`, {}, AUTH_TIMEOUT_MS);
                if (!response.ok) {
                    continue;
                }
                const data = await response.json();
                const required = data.auth_required === true;
                try {
                    localStorage.setItem('editoor_auth_required', String(required));
                } catch (e) {
                    // localStorage might be unavailable
                }
                return required;
            } catch (error) {
                console.error(`Failed to check auth status (attempt ${attempt + 1}/${attempts}):`, error);
            }
        }

        try {
            const cached = localStorage.getItem('editoor_auth_required');
            if (cached !== null) {
                console.warn('[AUTH] Status check failed, using cached auth_required =', cached);
                return cached === 'true';
            }
        } catch (e) {
            // localStorage might be unavailable
        }

        console.warn('[AUTH] Status check failed with no cached value - requiring auth');
        return true;
    }

    // Validate API token and get JWT. `rejected` is true only when the server
    // answered that the token is invalid (401), not when it could not answer.
    async validateToken(apiToken: string, saveToken: boolean = true): Promise<{ success: boolean; error?: string; rejected?: boolean }> {
        try {
            const response = await fetchWithTimeout(`${this.baseUrl}/php/auth_validate.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: apiToken })
            }, AUTH_TIMEOUT_MS);

            const data = await response.json();

            if (response.ok && data.success) {
                this.saveJWT(data.jwt);
                if (saveToken) {
                    this.saveAPIToken(apiToken);
                    this.updateAuthUI();
                }
                return { success: true };
            } else {
                return {
                    success: false,
                    error: data.error || 'Invalid token',
                    rejected: response.status === 401
                };
            }
        } catch (error) {
            return {
                success: false,
                error: 'Network error: ' + (error as Error).message,
                rejected: false
            };
        }
    }

    // Refresh JWT using stored API token
    async refreshJWT(): Promise<boolean> {
        return (await this.refreshWithStoredToken()).success;
    }

    private async refreshWithStoredToken(): Promise<{ success: boolean; rejected: boolean }> {
        if (!this.apiToken) {
            return { success: false, rejected: false };
        }

        console.log('[AUTH] Refreshing JWT using stored API token');
        const result = await this.validateToken(this.apiToken, false);
        if (result.success) {
            this.updateAuthUI();
        }
        return { success: result.success, rejected: result.rejected === true };
    }

    // Get JWT for use in requests
    public getJWT(): string | null {
        return this.jwt;
    }

    // Check if we have a valid JWT
    public hasJWT(): boolean {
        return this.jwt !== null && this.jwt !== '';
    }

    // Decode JWT payload (without validation)
    private decodeJWT(token: string): any | null {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }
            const payload = parts[1];
            const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            return JSON.parse(decoded);
        } catch (error) {
            console.error('[AUTH] Failed to decode JWT:', error);
            return null;
        }
    }

    // Get token name from JWT (for authenticated users)
    public getTokenName(): string | null {
        if (!this.hasJWT()) {
            return null;
        }
        const payload = this.decodeJWT(this.jwt!);
        return payload?.token_name || null;
    }

    // Show auth modal
    // If modal elements aren't found (e.g., in plugins), asks shell to show it
    public showAuthModal(): Promise<void> {
        return new Promise((resolve, reject) => {
            const modal = document.getElementById('authModal');
            const tokenInput = document.getElementById('authTokenInput') as HTMLInputElement;
            const inviteInput = document.getElementById('authInviteInput') as HTMLInputElement;
            const submitBtn = document.getElementById('authSubmitBtn');
            const errorDiv = document.getElementById('authError');
            const successDiv = document.getElementById('authSuccess');

            if (!modal || !tokenInput || !submitBtn || !errorDiv) {
                // Modal not available in this app - ask shell to show it
                console.log('[AUTH] Auth required, asking shell to show modal');
                window.parent.postMessage({
                    type: 'shell:showAuthModal'
                }, window.location.origin);

                // Listen for auth completion from shell
                const handleAuthComplete = (event: MessageEvent) => {
                    if (event.origin !== window.location.origin) return;
                    if (event.data?.type === 'shell:authComplete') {
                        window.removeEventListener('message', handleAuthComplete);
                        if (event.data.success) {
                            // Reload auth state
                            this.loadJWT();
                            this.loadAPIToken();
                            resolve();
                        } else {
                            reject(new Error('Authentication cancelled'));
                        }
                    }
                };
                window.addEventListener('message', handleAuthComplete);
                return;
            }

            // Show modal
            modal.style.display = 'block';
            tokenInput.value = '';
            if (inviteInput) inviteInput.value = '';
            errorDiv.style.display = 'none';
            if (successDiv) successDiv.style.display = 'none';
            tokenInput.focus();

            const handleSubmit = async () => {
                // Check which tab is active
                const tokenForm = document.getElementById('tokenAuthForm');
                const isTokenTab = tokenForm?.style.display !== 'none';

                if (isTokenTab) {
                    // Token login flow
                    const token = tokenInput.value.trim();

                    if (!token) {
                        errorDiv.textContent = 'Please enter a token';
                        errorDiv.style.display = 'block';
                        return;
                    }

                    submitBtn.textContent = 'Authenticating...';
                    (submitBtn as HTMLButtonElement).disabled = true;

                    const result = await this.validateToken(token);

                    if (result.success) {
                        modal.style.display = 'none';
                        resolve();
                    } else {
                        errorDiv.textContent = result.error || 'Authentication failed';
                        errorDiv.style.display = 'block';
                        submitBtn.textContent = 'Login';
                        (submitBtn as HTMLButtonElement).disabled = false;
                        tokenInput.select();
                    }
                } else {
                    // Invite code flow
                    if (!inviteInput) return;

                    const code = inviteInput.value.trim();
                    const tokenNameInput = document.getElementById('authTokenNameInput') as HTMLInputElement;
                    const tokenName = tokenNameInput?.value.trim() || 'API Token';

                    if (!code) {
                        errorDiv.textContent = 'Please enter an invite code';
                        errorDiv.style.display = 'block';
                        return;
                    }

                    submitBtn.textContent = 'Claiming...';
                    (submitBtn as HTMLButtonElement).disabled = true;

                    try {
                        const response = await fetchWithTimeout(`${this.baseUrl}/php/auth_invite.php?action=claim`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ code, token_name: tokenName })
                        }, AUTH_TIMEOUT_MS);

                        const data = await response.json();

                        if (response.ok && data.success) {
                            // Save the token and validate it
                            await this.validateToken(data.token);

                            // Show success message with the token. Built via
                            // DOM setters - server data must never be
                            // interpolated into innerHTML.
                            if (successDiv) {
                                successDiv.textContent = '';
                                const heading = document.createElement('strong');
                                heading.textContent = 'Success! Your API token:';
                                successDiv.appendChild(heading);
                                successDiv.appendChild(document.createElement('br'));

                                const tokenField = document.createElement('input');
                                tokenField.type = 'text';
                                tokenField.readOnly = true;
                                tokenField.value = data.token;
                                tokenField.style.cssText = 'width: 100%; padding: 8px; margin: 10px 0; border: 1px solid #27ae60; border-radius: 4px; font-family: monospace; font-size: 12px;';
                                tokenField.addEventListener('click', () => tokenField.select());
                                successDiv.appendChild(tokenField);

                                const hint = document.createElement('small');
                                hint.textContent = "Click to select and copy. Save this token securely - you won't be able to see it again!";
                                successDiv.appendChild(hint);
                                successDiv.style.display = 'block';
                            }
                            errorDiv.style.display = 'none';

                            // Change submit button to "Close"
                            submitBtn.textContent = 'Close';
                            (submitBtn as HTMLButtonElement).disabled = false;
                            submitBtn.onclick = () => {
                                modal.style.display = 'none';
                                resolve();
                            };
                        } else {
                            errorDiv.textContent = data.error || 'Failed to claim invite code';
                            errorDiv.style.display = 'block';
                            submitBtn.textContent = 'Claim Invite';
                            (submitBtn as HTMLButtonElement).disabled = false;
                            inviteInput.select();
                        }
                    } catch (error) {
                        errorDiv.textContent = 'Network error: ' + (error as Error).message;
                        errorDiv.style.display = 'block';
                        submitBtn.textContent = 'Claim Invite';
                        (submitBtn as HTMLButtonElement).disabled = false;
                        inviteInput.select();
                    }
                }
            };

            submitBtn.onclick = handleSubmit;

            // Allow Enter key to submit for both inputs
            tokenInput.onkeydown = (e) => {
                if (e.key === 'Enter') {
                    handleSubmit();
                }
            };

            if (inviteInput) {
                inviteInput.onkeydown = (e) => {
                    if (e.key === 'Enter') {
                        handleSubmit();
                    }
                };
            }
        });
    }

    // Initialize authentication flow
    public async initialize(): Promise<void> {
        const authRequired = await this.checkAuthStatus();

        if (!authRequired) {
            console.log('[AUTH] Authentication not required');
            return;
        }

        console.log('[AUTH] Authentication required');

        // Check if we have a JWT
        if (this.hasJWT()) {
            console.log('[AUTH] Using existing JWT (server will validate)');
            return;
        }

        // No JWT - try to get one using stored API token
        if (this.apiToken) {
            console.log('[AUTH] No JWT but API token found, attempting to refresh');
            const refreshed = await this.refreshWithStoredToken();
            if (refreshed.success) {
                console.log('[AUTH] JWT refreshed successfully');
                return;
            }
            if (!refreshed.rejected) {
                // Server unreachable or failing: keep the token, the next
                // 401 refreshes again once the server answers.
                DebugLogger.log('network', 'JWT refresh failed without a rejection, keeping the API token');
                return;
            }
            DebugLogger.log('state', 'Stored API token was rejected, clearing it');
            this.clearAPIToken();
        }

        // Show auth modal
        console.log('[AUTH] Showing auth modal');
        await this.showAuthModal();
    }

    // Add Authorization header to fetch requests
    public addAuthHeader(headers: HeadersInit = {}): HeadersInit {
        if (this.jwt) {
            return {
                ...headers,
                'Authorization': `Bearer ${this.jwt}`
            };
        }
        return headers;
    }

    // Flag to prevent concurrent refresh attempts
    private refreshPromise: Promise<{ success: boolean; rejected: boolean }> | null = null;

    /**
     * Authenticated fetch with automatic JWT refresh on 401.
     * If the request fails with 401, attempts to refresh the JWT using the stored API token.
     * If refresh succeeds, retries the original request.
     * If refresh fails, clears tokens and returns the 401 response.
     */
    public async authenticatedFetch(
        url: string,
        options: RequestInit = {},
        timing: AuthFetchTiming = {}
    ): Promise<Response> {
        return (await this.authenticatedFetchWithOutcome(url, options, timing)).response;
    }

    /**
     * authenticatedFetch, also telling whether a 401 stayed because the JWT
     * refresh failed without a rejection (server unreachable or failing). Then
     * the stored API token may still be good and a new login would not help.
     */
    private async authenticatedFetchWithOutcome(
        url: string,
        options: RequestInit,
        timing: AuthFetchTiming
    ): Promise<{ response: Response; refreshUnavailable: boolean }> {
        // Add auth header to the request
        const headers = this.addAuthHeader(options.headers || {});
        const requestOptions: RequestInit = { ...options, headers };

        // Make the initial request
        let response = await fetchWithTimeout(url, requestOptions, timing.timeoutMs);

        // If not 401, return the response as-is
        if (response.status !== 401) {
            return { response, refreshUnavailable: false };
        }

        // 401 received - attempt to refresh JWT if we have an API token
        if (!this.apiToken) {
            console.log('[AUTH] 401 received but no API token available for refresh');
            this.clearJWT();
            return { response, refreshUnavailable: false };
        }

        console.log('[AUTH] 401 received, attempting to refresh JWT');

        // Use a single refresh promise to prevent concurrent refresh attempts
        if (!this.refreshPromise) {
            this.refreshPromise = this.refreshWithStoredToken().finally(() => {
                this.refreshPromise = null;
            });
        }

        const refreshed = await this.refreshPromise;

        if (!refreshed.success) {
            if (refreshed.rejected) {
                DebugLogger.log('state', 'Stored API token was rejected, clearing tokens');
                this.clearAPIToken();
                return { response, refreshUnavailable: false };
            }
            DebugLogger.log('network', 'JWT refresh failed without a rejection, keeping the API token');
            this.clearJWT();
            return { response, refreshUnavailable: true };
        }

        console.log('[AUTH] JWT refreshed successfully, retrying request');

        // Retry the original request with the new JWT
        const retryHeaders = this.addAuthHeader(options.headers || {});
        const retryOptions: RequestInit = { ...options, headers: retryHeaders };
        response = await fetchWithTimeout(url, retryOptions, timing.timeoutMs);

        return { response, refreshUnavailable: false };
    }

    /**
     * Complete authenticated fetch with full auth flow handling.
     * Handles:
     * - 428: Admin setup required (shows modal, retries)
     * - 401: Authentication required (attempts JWT refresh, shows auth modal if needed, retries)
     * - Error toasts for failures
     *
     * @param url - The URL to fetch
     * @param options - Fetch options
     * @param showToasts - Whether to show error toasts (default: true)
     * @returns The response, or throws AuthError if auth completely fails
     */
    public async fetchWithFullAuth(
        url: string,
        options: RequestInit = {},
        showToasts: boolean = true,
        timing: AuthFetchTiming = {}
    ): Promise<Response> {
        try {
            // Make the initial authenticated request (handles JWT refresh automatically)
            let outcome = await this.authenticatedFetchWithOutcome(url, options, timing);

            // Handle 428 (Precondition Required) - admin setup needed
            if (outcome.response.status === 428) {
                const data = await outcome.response.json();
                if (data.requires_setup) {
                    console.log('[AUTH] Admin setup required');
                    await this.showAdminSetupModal();
                    console.log('[AUTH] Admin setup completed, retrying request');
                    outcome = await this.authenticatedFetchWithOutcome(url, options, timing);
                }
            }

            // Handle 401 - auth required (JWT refresh already attempted by authenticatedFetch)
            if (outcome.response.status === 401) {
                // The JWT refresh could not reach a verdict: the server is
                // unreachable or failing, so logging in again would not help.
                if (!outcome.refreshUnavailable) {
                    console.log('[AUTH] Authentication required, showing auth modal');

                    // Show auth modal to get credentials
                    await this.initialize();
                    console.log('[AUTH] Authentication completed, retrying request');

                    // Retry with new auth
                    outcome = await this.authenticatedFetchWithOutcome(url, options, timing);
                }

                if (outcome.refreshUnavailable) {
                    if (showToasts) {
                        this.showErrorToast('The server could not verify your login. Please try again later.');
                    }
                    throw new AuthError('Login could not be verified: JWT refresh failed without a rejection', 401);
                }

                // If still 401, auth has completely failed
                if (outcome.response.status === 401) {
                    if (showToasts) {
                        this.showErrorToast('Authentication failed. Please try logging in again.');
                    }
                    throw new AuthError('Authentication failed after retry', 401);
                }
            }

            // Handle server errors. Gateway errors are excluded: they mean the
            // backend is down, which callers treat as "offline, use the cache"
            // - telling the user "Server error, please try again later" would
            // contradict the offline indicator they are already being shown.
            const { response } = outcome;
            if (response.status >= 500 && !isServerDownStatus(response.status) && showToasts) {
                this.showErrorToast('Server error. Please try again later.');
            }

            return response;

        } catch (error) {
            // Re-throw AuthError as-is
            if (error instanceof AuthError) {
                throw error;
            }

            // Network or other errors. A network-level failure is the offline
            // case; callers surface it through the connection indicator, so do
            // not also raise a toast about it.
            DebugLogger.log('error', 'Auth request failed', { error: String(error) });
            if (showToasts && !isNetworkError(error)) {
                this.showErrorToast('Failed to connect to server. Please check your connection.');
            }
            throw error;
        }
    }

    /**
     * Helper to show error toast if Toast is available
     */
    private showErrorToast(message: string): void {
        // Dynamically check if Toast is available (avoid circular dependency)
        const Toast = (window as any).Toast;
        if (Toast && typeof Toast.show === 'function') {
            Toast.show(message, 'error');
        } else {
            // console.* is invisible on Android/Tauri (CLAUDE.md rule 2).
            DebugLogger.log('error', `[AUTH] ${message}`);
        }
    }

    /**
     * Convenience method to initialize a database with full auth handling.
     * This is the one-liner that apps should use.
     *
     * @param fileId - The file ID for the database
     * @returns Object containing success status and optional write token for new databases
     */
    public async initializeDatabase(fileId: string): Promise<{ ok: boolean; writeToken?: string | null; appendOnlyToken?: string | null; error?: string; unreachable?: boolean }> {
        // In Tauri mode, database is initialized by YjsSync via StorageProvider
        if (isTauri()) {
            return { ok: true, writeToken: null, appendOnlyToken: null };
        }

        const initUrl = `${this.baseUrl}/php/init_database.php?fileid=${fileId}`;

        let response: Response;
        try {
            // On the open path: timed (AUTH_TIMEOUT_MS).
            response = await this.fetchWithFullAuth(initUrl, {}, true, { timeoutMs: AUTH_TIMEOUT_MS });
        } catch (err) {
            // The request never reached the server. This is not an error the
            // user needs to act on - callers can carry on from the IndexedDB
            // cache - so it is reported separately from a server-side failure.
            if (isNetworkError(err)) {
                return { ok: false, unreachable: true, error: 'Server unreachable' };
            }
            throw err;
        }

        if (!response.ok) {
            // A gateway error means the proxy is up but the backend is not, so
            // the request never reached PHP. Same situation as a rejected
            // fetch, and the common one behind a reverse proxy.
            if (isServerDownStatus(response.status)) {
                return { ok: false, unreachable: true, error: `Server unreachable (HTTP ${response.status})` };
            }
            const data = await response.json().catch(() => ({}));
            return { ok: false, error: data.error || 'Database initialization failed' };
        }

        const data = await response.json();
        return {
            ok: data.success,
            writeToken: data.writeToken || null,
            appendOnlyToken: data.appendOnlyToken || null,
            error: data.error
        };
    }

    // Regenerate token (for security - invalidates old token)
    public async regenerateToken(): Promise<{ success: boolean; token?: string; error?: string }> {
        if (!this.jwt) {
            return { success: false, error: 'Not authenticated' };
        }

        try {
            const response = await fetchWithTimeout(`${this.baseUrl}/php/auth_regenerate_token.php`, {
                method: 'POST',
                headers: this.addAuthHeader({
                    'Content-Type': 'application/json'
                })
            }, AUTH_TIMEOUT_MS);

            const data = await response.json();

            if (response.ok && data.success) {
                // Update stored JWT and token
                this.saveJWT(data.jwt);
                this.saveAPIToken(data.token);
                this.updateAuthUI();
                return { success: true, token: data.token };
            } else {
                return {
                    success: false,
                    error: data.error || 'Failed to regenerate token'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: 'Network error: ' + (error as Error).message
            };
        }
    }

    // Admin token strength check - mirrors the server-side rule in
    // php/admin_auth_helper.php (isStrongAdminToken)
    public static isStrongAdminToken(token: string): boolean {
        if (token.length < 16) return false;
        if (new Set(token).size < 8) return false;
        if (token.length >= 24) return true;
        let classes = 0;
        if (/[a-z]/.test(token)) classes++;
        if (/[A-Z]/.test(token)) classes++;
        if (/[0-9]/.test(token)) classes++;
        if (/[^a-zA-Z0-9]/.test(token)) classes++;
        return classes >= 3;
    }

    // Show admin setup modal for first-time configuration
    // If modal elements aren't found (e.g., in doc/sheet apps), redirects to Drive
    public showAdminSetupModal(): Promise<void> {
        return new Promise((resolve, _reject) => {
            const modal = document.getElementById('adminSetupModal');
            const tokenInput = document.getElementById('adminSetupInput') as HTMLInputElement;
            const confirmInput = document.getElementById('adminSetupConfirm') as HTMLInputElement;
            const submitBtn = document.getElementById('adminSetupSubmitBtn');
            const errorDiv = document.getElementById('adminSetupError');
            const successDiv = document.getElementById('adminSetupSuccess');
            const goToAdminBtn = document.getElementById('adminSetupGoToAdmin');
            const continueBtn = document.getElementById('adminSetupContinue');

            if (!modal || !tokenInput || !confirmInput || !submitBtn || !errorDiv || !successDiv || !goToAdminBtn || !continueBtn) {
                // Modal not available in this app - redirect to home page for setup
                console.log('[AUTH] Admin setup required but modal not available, redirecting to home');
                window.location.href = this.baseUrl + '/';
                // Don't resolve or reject - we're navigating away
                return;
            }

            // Show modal
            modal.style.display = 'block';
            tokenInput.value = '';
            confirmInput.value = '';
            errorDiv.style.display = 'none';
            successDiv.style.display = 'none';
            tokenInput.focus();

            const handleSubmit = async () => {
                const token = tokenInput.value;
                const confirm = confirmInput.value;

                if (!token || !AuthManager.isStrongAdminToken(token)) {
                    errorDiv.textContent = 'Admin token too weak: use at least 16 characters mixing 3+ character types, or 24+ varied characters';
                    errorDiv.style.display = 'block';
                    return;
                }

                if (token !== confirm) {
                    errorDiv.textContent = 'Tokens do not match';
                    errorDiv.style.display = 'block';
                    return;
                }

                submitBtn.textContent = 'Setting up...';
                (submitBtn as HTMLButtonElement).disabled = true;

                try {
                    const response = await fetchWithTimeout(`${this.baseUrl}/php/auth_setup_admin.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ admin_token: token })
                    }, AUTH_TIMEOUT_MS);

                    const data = await response.json();

                    if (response.ok && data.success) {
                        console.log('[AUTH] Admin token configured successfully');

                        // Hide form elements and show success options
                        (submitBtn as HTMLElement).style.display = 'none';
                        (tokenInput.parentElement as HTMLElement).style.display = 'none';
                        (confirmInput.parentElement as HTMLElement).style.display = 'none';
                        successDiv.style.display = 'block';
                    } else {
                        errorDiv.textContent = data.error || 'Setup failed';
                        errorDiv.style.display = 'block';
                        submitBtn.textContent = 'Set Admin Token';
                        (submitBtn as HTMLButtonElement).disabled = false;
                    }
                } catch (error) {
                    errorDiv.textContent = 'Network error: ' + (error as Error).message;
                    errorDiv.style.display = 'block';
                    submitBtn.textContent = 'Set Admin Token';
                    (submitBtn as HTMLButtonElement).disabled = false;
                }
            };

            // Handle "Go to Admin Panel" button
            goToAdminBtn.onclick = () => {
                window.location.href = 'admin.html';
                // Don't call resolve() - we're navigating away
            };

            // Handle "Continue Creating Document" button
            continueBtn.onclick = () => {
                modal.style.display = 'none';
                resolve();
            };

            submitBtn.onclick = handleSubmit;

            // Allow Enter key to submit
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    handleSubmit();
                }
            };

            tokenInput.onkeydown = handleKeyDown;
            confirmInput.onkeydown = handleKeyDown;
        });
    }
}
